import { EditorClose, editorSaved } from '@sensika/sui/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { VIEW_PRESET_QUERY_PARAM } from '../../utils/view-preset.constants';
import {
  EMPTY_CELL_CLASS,
  EMPTY_VALUE_PLACEHOLDER,
  isEmptyCellValue,
  isEmptyNumeric,
  isNumericColumn,
} from '../../utils/empty-value.utils';
import { withFilterResetButton } from '../../utils/filter-buttons.utils';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  distinctUntilChanged,
  filter,
  skip,
  switchMap,
  tap,
} from 'rxjs';
import {
  CellFocusedEvent,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  ColumnState,
  FilterModel,
  GridApi,
  GridOptions,
  IRowNode,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  ITooltipParams,
  LoadSuccessParams,
  RowClickedEvent,
  SortModelItem,
  StateUpdatedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Signal,
  SimpleChanges,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  DATA_GRID_ADAPTER,
  GridViewPreset,
  GridViewPresetScope,
  noViewPresetStore,
} from '../../data-grid.adapter';

import { agGridBodyPopupParent } from '../../utils/popup-parent.utils';
import { PAGE_SELECT_COLUMN_DEF } from '../ag-page-select-header/ag-page-select-header.component';
import {
  GridToolbarComponent,
  GridToolbarTab,
} from '../grid-toolbar/grid-toolbar.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { sortDeepForComparison } from '../../utils/object-compare.utils';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DialogCloseEvent, DialogService } from '@sensika/sui/dialog';
import { ComboboxOption } from '@sensika/sui/combobox';

export interface GridTabItem {
  id: string;
  label: string;
  icon: IconProp;
  path?: Array<string | number>; // mixed because connector.id may be number
  is_preset: boolean;
  /** A pinned System / Company preset: shown as a tab, but the caller cannot unpin it. */
  is_locked?: boolean;
  disabled?: boolean;
}

/** A preset's `settings` blob: ag-grid column state + filter model plus any view-specific keys. */
export interface ViewPresetSettings {
  columnState?: ColumnState[];
  filterModel?: { [colId: string]: any } | null;
  [key: string]: unknown;
}

/** Comparable form of `ViewPresetSettings`: `core` = columns + filters, `extra` = the rest or `null` when absent. */
interface ViewPresetSettingsSnapshot {
  core: string;
  extra: string | null;
}

/** A 403 from the grid endpoint is a permission answer, not a load failure. */
const HTTP_FORBIDDEN = 403;

/** The row-selection checkbox column: a click there toggles the bulk scope, never the current row. */
const SELECTION_CHECKBOX_SELECTOR =
  '.ag-selection-checkbox, .ag-checkbox-input-wrapper, [col-id="ag-Grid-SelectionColumn"]';

/**
 * Compositional grid view: owns the template (toolbar, view presets, SSRM
 * datasource, pagination) and takes everything view-specific as typed inputs,
 * callbacks and content slots (`toolbarActions`, `belowToolbar`, `sideRegion`).
 * Feature components wrap it; nothing extends it.
 */
@Component({
  selector: 'app-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataGridComponent {
  // ---- Identity ----
  @Input({ required: true }) viewId: string;
  @Input({ required: true }) title = '';
  @Input() icon: IconProp;

  // ---- Data ----
  @Input({ required: true }) columnDefs: Array<ColDef | ColGroupDef> = [];
  /** One page of rows plus the backend total (`rowCount` drives the paginator). */
  @Input({ required: true }) fetchRows: (
    request: IServerSideGetRowsRequest,
  ) => Observable<LoadSuccessParams>;

  // ---- Behavior ----
  /** Off for views whose endpoint cannot report a real total (keeps infinite scroll). */
  @Input() paginated = true;
  /** Extra class on the grid container (borrowed skins, per-view cell styling hooks). */
  @Input() gridClassName = '';
  /**
   * Shallow-merged over the base options after construction, before the grid
   * renders. For ag-grid's own surface (getRowId, overlayNoRowsTemplate, event
   * hooks) - the SSRM/datasource/pagination wiring stays owned here.
   */
  @Input() gridOptionsOverrides: Partial<GridOptions> | null = null;
  /**
   * Opt-in for grids whose backend filters rows through the user's object
   * permissions (getPermissionSolrIndexQuery): the "View All Company Objects"
   * toggle in the user dropdown changes that filter, so a mounted grid must
   * refetch when the flag flips. The dropdown broadcasts the server-confirmed
   * flag into the auth store, so the purge always fetches under the new scope.
   */
  @Input() refreshOnCompanyObjectsToggle = false;
  // Client-side export dumps only the loaded grid rows - not authoritative.
  // Only the 3 clientSide grids (whose rows ARE the full dataset) may opt in;
  // every other view exposes Export solely through a backend CSV endpoint.
  @Input() csvClientSide = false;
  @Input() csvServerSide = false;
  /**
   * Master-detail: the current row (clicked, or holding the focused cell after
   * keyboard navigation) is emitted through `selectedRowDataChange` and marked
   * like a selected one; drives a host detail panel in `sideRegion`.
   */
  @Input() trackRowSelection = false;
  /**
   * Multi-row checkbox selection (WAP-1729): the ids feed a bulk-action scope.
   * `app-grid-selection` in `[toolbarStatus]` owns the pill and the ids; the
   * selection is dropped on a filter change (the component) and on every purge
   * (`purgeRows()`), never on an in-place `refreshAfterEdit()`. No native
   * header checkbox: under the Server-Side
   * Row Model ag-grid ignores `selectAll: 'currentPage'` (warning 195) and the
   * header box sets an "all rows" state while getSelectedRows() only returns
   * the loaded rows (warning 199) - "whole filtered dataset" is the bulk
   * dialogs' "All matching filters" scope instead (WAP-1666). A paginated grid
   * gets the page-scoped `AgPageSelectHeaderComponent` as its header (WAP-1750).
   */
  @Input() checkboxSelection = false;
  /** Route data key for views whose editor swaps in via child routes (setupRouting). */
  @Input() routedEditorObjectName: string | null = null;

  // ---- View-specific preset settings ----
  /**
   * View-specific keys a preset carries beside `columnState` / `filterModel`
   * (e.g. the sidebar widget selection of a data view). A signal, so the live
   * snapshot re-derives the Save state on its own when the host's state changes;
   * `null` when the view has none.
   */
  @Input() extraViewPresetSettings: Signal<{
    [key: string]: unknown;
  } | null> | null = null;
  /**
   * Counterpart of `extraViewPresetSettings`: restores the view-specific state
   * from an applied preset's `settings` (or the per-browser copy on a plain
   * refresh). A preset saved before a key existed omits it - leave that state
   * untouched.
   */
  @Input() onApplyExtraViewPresetSettings:
    | ((settings: { [key: string]: unknown }) => void)
    | null = null;

  // ---- Callbacks ----
  /** Replaces the toolbar Export action (e.g. server-streamed CSV of the whole resultset). */
  @Input() onExportCsv: (() => void) | null = null;
  @Input() onExportCsvServerSide: (() => void) | null = null;
  /** Toolbar Refresh drops feature-level caches through this before purging the row cache. */
  @Input() onInvalidateCaches: (() => void) | null = null;

  @Output() selectedRowDataChange = new EventEmitter<unknown | null>();
  /** Every row click, regardless of trackRowSelection. */
  @Output() rowClicked = new EventEmitter<RowClickedEvent>();
  /** Editable-cell commits (sysadmin translation/nomenclature grids). */
  @Output() cellValueChanged = new EventEmitter<CellValueChangedEvent>();

  /**
   * The live ag-grid API - the escape hatch for host-level grid work the
   * wrapper has no method for. Prefer `refreshRows()`, `applyFilterModel()`,
   * `filterModel()`, `requestSnapshot()`, `clearSelection()` (WAP-1726),
   * `selectedIds()` and `quickSearchQuery()` (WAP-1739).
   */
  get api(): GridApi | undefined {
    return this.gridApi;
  }

  /**
   * The last SSRM request the datasource sent, whole. For an endpoint that takes
   * the full request (the Admin grids' `?output=csv` replay). An endpoint or a
   * dialog that takes filter + sort only uses `requestSnapshot()` instead.
   */
  get lastRequest(): IServerSideGetRowsRequest | null {
    return this.lastGetRowsParams?.request ?? null;
  }

  private readonly toolbar = viewChild(GridToolbarComponent);
  /**
   * True while the toolbar strip has collapsed its labelled buttons to icons.
   * Hosts mirror it on the buttons they project into `[toolbarActions]`:
   * `<app-data-grid #grid>` + `[iconOnly]="grid.toolbarCollapsed()"`.
   */
  readonly toolbarCollapsed = computed(
    () => this.toolbar()?.collapsed() ?? false,
  );

  // ---- AG Grid state ----
  /** API for interacting with the AG Grid instance. */
  gridApi!: GridApi;

  /** Options for configuring the AG Grid instance. */
  gridOptions: GridOptions;

  /** Row node id the details pane currently shows (`trackRowSelection`). */
  private currentRowId: string | null = null;

  /** The current row's data while `trackRowSelection` is on. */
  protected selectedObject: any;

  private readonly adapter = inject(DATA_GRID_ADAPTER);

  /** Number of rows in the table. */
  protected rowCount: number = 0;

  /** A signal, not a plain flag: `getRows` runs outside the Angular zone, so the response callback
   *  schedules no change detection on its own and a plain `false` left the toolbar spinner up (WAP-1746). */
  protected readonly loading = signal(false);

  // ---- Pagination ----
  // * Feed-style page jumping over the server-side row model: ag-grid's own
  // * panel stays suppressed and the toolbar's <sui-pagination> drives pages
  // * through paginationGoToPage. pageSize matches cacheBlockSize so one page
  // * is exactly one block fetch.
  /** Rows per page; kept in step with `cacheBlockSize`. */
  readonly pageSize = 100;
  /** 1-based page the paginator shows. */
  readonly currentPage = signal(1);
  /** Total filtered rows as reported by the backend, 0 until the first fetch. */
  readonly totalCount = signal(0);

  private readonly checkboxSelectedIds = signal<string[]>([]);
  /**
   * Row ids (`getRowId` strings) of the checkbox-selected rows (WAP-1739); `[]`
   * without `checkboxSelection`. Emptied on `filterChanged` and on every purge:
   * a selection is a bulk scope on one result set. The pill and the bulk menu
   * read it through DI; a host with integer ids maps where it builds its scope.
   */
  readonly selectedIds = this.checkboxSelectedIds.asReadonly();
  readonly selectionCount = computed(() => this.checkboxSelectedIds().length);

  private readonly submittedQuickSearch = signal('');
  /**
   * The submitted quick-search query, trimmed, `''` when empty (WAP-1739). Set
   * by the projected `app-grid-quick-search`; the host reads it into its request
   * context, the Data View pane and the filter chips read it through DI.
   */
  readonly quickSearchQuery = this.submittedQuickSearch.asReadonly();

  /** The quick search's submit: a new query re-queries the rows (SSRM purge). */
  setQuickSearchQuery(query: string): void {
    if (query === this.submittedQuickSearch()) {
      return;
    }
    this.submittedQuickSearch.set(query);
    this.refreshRows();
  }

  protected tabs: GridTabItem[] = [];
  /** The tab strip as `app-grid-toolbar` renders it: pinned presets can be unpinned in place. */
  protected get toolbarTabs(): GridToolbarTab[] {
    return this.tabs.map((tab) => ({
      id: tab.id,
      label: tab.label,
      removable: tab.is_preset && !tab.is_locked,
    }));
  }

  protected currentTabId: string;

  protected objectEditorMode: boolean = false;

  protected displayMode: 'list' | 'edit' | 'create' = 'list';

  protected object: unknown;

  protected lastGetRowsParams: IServerSideGetRowsParams | null = null;

  // ─── View presets (Angular Query + signals) ───────────────────────────
  // `viewId` is an input, final only in ngOnInit, so the query is keyed on a
  // signal that ngOnInit seeds; until then it is disabled.
  protected readonly presetViewId = signal<string | null>(null);
  private readonly presetStore =
    this.adapter.viewPresets?.(this.presetViewId) ?? noViewPresetStore();

  protected readonly GridViewPresetScope = GridViewPresetScope;
  readonly canShareWithCompany = computed(
    () => this.adapter.user().canManageCompanyPresets,
  );
  readonly canShareWithSystem = computed(
    () => this.adapter.user().canManageSystemPresets,
  );

  /**
   * The star and the pin write the selected row's flags: always on the caller's own
   * preset, on a shared one only for a caller who may manage that scope (WAP-1778).
   * `true` with nothing selected so the disabled buttons keep their place.
   */
  readonly canEditSelectedViewPresetFlags = computed(() => {
    const preset = this.selectedViewPreset();
    if (!preset?.is_locked) {
      return true;
    }
    return preset.view_preset_scope_id === GridViewPresetScope.SYSTEM
      ? this.canShareWithSystem()
      : this.canShareWithCompany();
  });

  readonly availableViewPresets = computed<GridViewPreset[]>(
    () => this.presetStore.presets() ?? [],
  );
  // sui-combobox option list grouped System / Company / Mine (the combobox renders
  // groups in first-seen order, hence the sort); the preset that opens the grid
  // carries the shared "(default)" suffix so it can be told apart without opening Edit.
  readonly viewPresetOptions = computed<ComboboxOption[]>(() =>
    [...this.availableViewPresets()]
      .sort(
        (a, b) =>
          (a.view_preset_scope_id ?? GridViewPresetScope.USER) -
          (b.view_preset_scope_id ?? GridViewPresetScope.USER),
      )
      .map((p) => ({
        label: this.viewPresetLabel(p),
        value: p.uuid,
        group: this.viewPresetGroupLabel(p),
      })),
  );
  // Selection is held by uuid and resolved against the cached list, so a preset
  // patched by a mutation (flag flip, save) is never read through a stale copy.
  readonly selectedViewPresetId = signal<string | null>(null);
  /** The `viewPreset` query param has been honoured - see estimateTabsFromViewPresets(). */
  private deepLinkedPresetApplied = false;
  /** The SSRM datasource is attached once, after the opening preset is resolved. */
  private datasourceAttached = false;
  readonly selectedViewPreset = computed<GridViewPreset | null>(
    () =>
      this.availableViewPresets().find(
        (p) => p.uuid === this.selectedViewPresetId(),
      ) ?? null,
  );
  // Dirtiness is DERIVED, never set: the live grid's normalized settings versus
  // the selected preset's (or, with no preset, versus the grid's own baseline).
  // Applying a preset therefore reads clean by construction, and undoing a change
  // by hand reads clean again - event counting could do neither.
  /** `true` once `onGridReady` ran: the live snapshot (and a capability beside the wrapper) can read the grid api. */
  readonly gridReady = signal(false);
  /** Bumped by every relevant ag-grid `stateUpdated`: the grid api is not reactive on its own. */
  private readonly gridStateVersion = signal(0);
  /**
   * Normalized live settings. A computed, so the view-specific part (the host's
   * `extraViewPresetSettings` signal) re-derives the Save state without the
   * host wiring anything.
   */
  private readonly gridSnapshot = computed<ViewPresetSettingsSnapshot | null>(
    () => {
      this.gridStateVersion();
      return this.gridReady() ? this.snapshotGridSettings() : null;
    },
  );
  /** The grid as it opens with no preset: what "changed" means when none is selected. */
  private baselineSnapshot: ViewPresetSettingsSnapshot | null = null;
  /**
   * False until the presets query has settled AND the grid exists, i.e. until
   * the default preset (if any) has been applied. The toolbar keeps the preset
   * controls neutral until then, so a view does not flash "nothing selected"
   * (Save visible, star/pin off) for the moment before the default lands.
   */
  readonly presetsInitialized = signal(false);
  /** True once the datasource answered for the first time (rows or a failure). */
  private readonly firstLoadSettled = signal(false);
  /** The toolbar's control strip renders only once the view is in its final state. */
  readonly toolbarReady = computed(
    () => this.firstLoadSettled() && this.presetsInitialized(),
  );
  readonly viewSettingsHasChanged = computed<boolean>(() => {
    const snapshot = this.gridSnapshot();
    if (!snapshot) {
      return false;
    }
    const preset = this.selectedViewPreset();
    const reference = preset
      ? this.normalizeViewPresetSettings(preset.settings)
      : this.baselineSnapshot;
    let changed = false;
    if (reference !== null) {
      // A preset saved before the view carried extra keys has nothing to compare
      // them against: it is never dirty on their account alone.
      changed =
        snapshot.core !== reference.core ||
        (reference.extra !== null && snapshot.extra !== reference.extra);
    }
    return changed;
  });

  isCreatingNew: boolean = false;

  protected destroyRef = inject(DestroyRef);
  protected dialogService = inject(DialogService);

  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    protected translateService: TranslateService,
  ) {
    this.gridOptions = this.getGridOptions();
    this.gridOptions.enableRtl = this.adapter.language().rtl;

    // skip(1) ignores the value present at mount - only a flip while this
    // grid is on screen refetches.
    toObservable(computed(() => this.adapter.user().listCompanyObjects))
      .pipe(
        distinctUntilChanged(),
        skip(1),
        filter(() => this.refreshOnCompanyObjectsToggle),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshGrid());

    // Pinned presets become tabs and the opening preset is applied whenever the
    // list changes (first load, save, delete). Applying needs the grid, so before
    // onGridReady this is a no-op and onGridReady runs the same sync itself. The
    // status is tracked too: a failed fetch leaves the list empty but must still
    // release the datasource (see attachServerSideDatasource).
    effect(() => {
      const presets = this.availableViewPresets();
      this.presetStore.status();
      untracked(() => {
        if (this.gridApi) {
          this.estimateTabsFromViewPresets(presets);
        }
      });
    });

    // The view's extra preset settings (e.g. the sidebar widget selection) are
    // remembered per browser so a plain refresh keeps them; gated on gridReady so
    // the restore in ngOnInit is never overwritten by the slice's initial state.
    effect(() => {
      const extra = this.currentExtraViewPresetSettings();
      if (this.gridReady()) {
        untracked(() => this.rememberExtraViewPresetSettings(extra));
      }
    });
  }

  /** The host's view-specific preset keys right now; `null` when the view has none. */
  private currentExtraViewPresetSettings(): { [key: string]: unknown } | null {
    return this.extraViewPresetSettings?.() ?? null;
  }

  /** Per-browser storage key of the view's extra preset settings; `null` before the view id is known. */
  private extraViewPresetSettingsStorageKey(): string | null {
    return this.viewId ? `sensika.grid.${this.viewId}.view-settings` : null;
  }

  private rememberExtraViewPresetSettings(
    extra: {
      [key: string]: unknown;
    } | null,
  ): void {
    const key = this.extraViewPresetSettingsStorageKey();
    if (key) {
      try {
        if (extra && Object.keys(extra).length > 0) {
          localStorage.setItem(key, JSON.stringify(extra));
        } else {
          localStorage.removeItem(key);
        }
      } catch (error) {
        this.adapter.logger.debug('View settings not remembered:', error);
      }
    }
  }

  /**
   * Re-applies the remembered extra settings before any preset lands. A default
   * preset applied later wins - `onSelectedGridSettingsChange` runs the same hook.
   */
  private restoreExtraViewPresetSettings(): void {
    const key = this.extraViewPresetSettingsStorageKey();
    if (key && this.onApplyExtraViewPresetSettings) {
      try {
        const raw = localStorage.getItem(key);
        const stored = raw ? JSON.parse(raw) : null;
        if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
          this.onApplyExtraViewPresetSettings(stored);
        }
      } catch (error) {
        this.adapter.logger.debug('Remembered view settings ignored:', error);
      }
    }
  }

  ngOnInit(): void {
    // getGridOptions() ran in the constructor, before inputs existed; the grid
    // itself renders only after this init, so patching the options object here
    // is early enough.
    this.gridOptions.pagination = this.paginated;
    if (this.checkboxSelection) {
      this.gridOptions.rowSelection = {
        mode: 'multiRow',
        checkboxes: true,
        headerCheckbox: false,
        enableClickSelection: false,
      };
      if (this.paginated) {
        this.gridOptions.selectionColumnDef = PAGE_SELECT_COLUMN_DEF;
      }
    }
    if (this.gridOptionsOverrides) {
      Object.assign(this.gridOptions, this.gridOptionsOverrides);
    }

    this.setupGridOptions();

    this.route.queryParams
      .pipe(
        tap(() => this.handleAgGridParamsChange()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Inputs are final by now.
    this.presetViewId.set(this.viewId ?? null);
    this.restoreExtraViewPresetSettings();
    this.gridOptions.columnDefs = withFilterResetButton(this.columnDefs);

    if (this.routedEditorObjectName) {
      this.setupRouting(this.routedEditorObjectName);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.handleAgGridParamsChange(changes);
    // Hosts with async column defs (nomenclature lookups, per-company columns)
    // pass a fresh array when it resolves; the initial value renders the grid.
    if (
      changes['columnDefs'] &&
      !changes['columnDefs'].firstChange &&
      this.gridApi
    ) {
      this.gridApi.setGridOption(
        'columnDefs',
        withFilterResetButton(this.columnDefs),
      );
    }
  }

  // ---- Host-facing surface ----

  /** Purge the row cache and refetch (dropping feature caches first). */
  refresh(): void {
    this.refreshGrid();
  }

  /**
   * Back-populates a saved edit into the rows on screen: re-fetches the loaded
   * blocks in place (no purge, scroll and selection survive). See architecture.md,
   * "Row models and editor dialogs".
   */
  refreshAfterEdit(): void {
    this.onInvalidateCaches?.();
    this.gridApi?.refreshServerSide({ purge: false });
  }

  /**
   * Refreshes the rows once the editor behind `close` saved (see `editorSaved`).
   * Every opener in a grid view passes its editor handle here instead of dropping it.
   */
  refreshWhenSaved(
    close: EditorClose | Observable<DialogCloseEvent<unknown>> | void,
  ): void {
    editorSaved(close).then((saved) => {
      if (saved && !this.gridApi?.isDestroyed()) {
        this.refreshAfterEdit();
      }
    });
  }

  /** Host-side close control for a selection-driven detail panel. */
  clearSelectedRow(): void {
    this.selectedObject = null;
    this.currentRowId = null;
    this.gridApi?.redrawRows();
    this.emitSelectedRow();
  }

  /**
   * Re-queries the rows (SSRM purge) after a host-driven change the grid cannot
   * see - a store filter, the global period, the quick search. Host caches
   * survive; `refresh()` is the one that also invalidates them.
   */
  refreshRows(): void {
    this.purgeRows();
  }

  /**
   * SSRM purge: the result set changes, so a checkbox selection (a bulk scope
   * on the previous set) is dropped with it; `refreshAfterEdit()` keeps it.
   */
  private purgeRows(): void {
    if (!this.gridApi) {
      return;
    }
    if (this.checkboxSelection) {
      this.gridApi.deselectAll();
    }
    this.gridApi.refreshServerSide({ purge: true });
  }

  /**
   * Replaces the column filters and re-queries the rows. An empty model clears
   * every filter (ag-grid ignores `{}`); the purge is explicit so the rows reload
   * even when only out-of-grid context changed alongside.
   */
  applyFilterModel(model: FilterModel | null): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.setFilterModel(
      model && Object.keys(model).length ? model : null,
    );
    this.purgeRows();
  }

  /** The live column filter model, `null` while the grid is not ready. */
  filterModel(): FilterModel | null {
    if (!this.gridApi) {
      return null;
    }
    return this.gridApi.getFilterModel() ?? {};
  }

  /**
   * The live filter + sort state, in SSRM request shape. For an endpoint or a
   * dialog that takes filter + sort with its own context (the explorers' CSV
   * stream, the bulk-action dialogs); `lastRequest` is the whole-request
   * counterpart. `null` while the grid is not ready.
   */
  requestSnapshot(): {
    filterModel: FilterModel;
    sortModel: SortModelItem[];
  } | null {
    if (!this.gridApi) {
      return null;
    }
    const sortModel: SortModelItem[] = (this.gridApi.getColumnState() ?? [])
      .filter((c) => c.sort)
      .map((c) => ({ colId: c.colId, sort: c.sort }));
    return { filterModel: this.gridApi.getFilterModel() ?? {}, sortModel };
  }

  /** Clears the checkbox (bulk-scope) selection; `clearSelectedRow()` is the master-detail row. */
  clearSelection(): void {
    this.gridApi?.deselectAll();
  }

  private readonly dataTable = viewChild<ElementRef<HTMLElement>>('dataTable');

  /**
   * Refit the columns whenever the grid's own box changes width - a side region
   * (Data View pane, detail panel) opening or closing, the main menu sliding.
   * Width only, so row-count driven height changes never refit; one refit per
   * animation frame.
   */
  ngAfterViewInit(): void {
    const el = this.dataTable()?.nativeElement;
    if (!el) {
      return;
    }
    let lastWidth = el.clientWidth;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      const width = el.clientWidth;
      if (width === lastWidth || width === 0) {
        return;
      }
      lastWidth = width;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.gridApi?.sizeColumnsToFit());
    });
    observer.observe(el);
    this.destroyRef.onDestroy(() => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    });
  }

  estimateTabsFromViewPresets(presets: GridViewPreset[]) {
    const pinned = presets.filter((viewPreset) => viewPreset.is_pinned);
    this.tabs = [
      ...this.tabs.filter((tab) => !tab.is_preset),
      ...pinned.map(
        (viewPreset): GridTabItem => ({
          id: viewPreset.uuid,
          label: this.viewPresetLabel(viewPreset),
          icon: 'table',
          is_preset: true,
          is_locked: viewPreset.is_locked ?? false,
        }),
      ),
    ];

    // With nothing selected, the preset that opens the grid is, in order: the
    // deep link (Admin -> View Presets; honoured once, on the first list that
    // holds it - afterwards the visit behaves like any other), the default, else
    // the first pinned preset - the tab the strip shows selected anyway, so the
    // rows and the highlighted tab never disagree.
    if (!this.currentTabId) {
      const requestedUuid = this.deepLinkedPresetApplied
        ? null
        : this.route.snapshot.queryParamMap.get(VIEW_PRESET_QUERY_PARAM);
      const requested = requestedUuid
        ? presets.find((p) => p.uuid === requestedUuid)
        : undefined;
      if (requested) {
        this.deepLinkedPresetApplied = true;
      }
      const opener = requested ?? this.resolveOpeningPreset(presets);
      if (opener) {
        this.onSelectedGridSettingsChange(opener);
      }
    }

    // A grid without a viewId never fetches (the query stays 'pending' while
    // disabled), so it is final immediately; otherwise wait for the answer. The
    // datasource is attached only now, so the first SSRM request already carries
    // the opening preset's columns, filters and sort instead of the column-def
    // defaults followed by a second request when the preset lands.
    if (!this.presetViewId() || this.presetStore.status() !== 'pending') {
      this.presetsInitialized.set(true);
      this.attachServerSideDatasource();
    }
  }

  /**
   * The preset a grid shows with nothing selected: the default, else the first
   * pinned one (the tab strip highlights its first tab whenever no tab is
   * active, so that preset is applied rather than shown as a false selection).
   */
  private resolveOpeningPreset(
    presets: GridViewPreset[],
    except?: string,
  ): GridViewPreset | undefined {
    const candidates = presets.filter((p) => p.uuid !== except);
    return (
      candidates.find((p) => p.is_effective_default) ??
      candidates.find((p) => p.is_pinned)
    );
  }

  /**
   * Nothing selected any more (combobox cleared, selected preset deleted):
   * back to the opening preset when there is one, else the grid's own baseline.
   * Resolved before touching the grid so the rows reload once, not reset-then-apply.
   */
  private applyOpeningPresetOrBaseline(except?: string): void {
    this.selectedViewPresetId.set(null);
    this.currentTabId = null;
    const opener = this.resolveOpeningPreset(
      this.availableViewPresets(),
      except,
    );
    if (opener) {
      this.onSelectedGridSettingsChange(opener);
    } else {
      this.resetGridSettings();
    }
  }

  /** Once per grid, after the opening preset is resolved (see estimateTabsFromViewPresets). */
  private attachServerSideDatasource(): void {
    if (this.datasourceAttached || !this.gridApi) {
      return;
    }
    this.datasourceAttached = true;
    this.gridApi.updateGridOptions({
      serverSideDatasource: this.createServerSideDatasource(),
    });
  }

  // sui-combobox emits the selected *value* (the uuid).
  // Resolve back to the preset and reuse the
  // existing handlers; a null value (cleared) maps to the old (clear) path.
  onViewPresetValueChange(uuid: string | null): void {
    if (!uuid) {
      this.onSelectedGridSettingsClear();
      return;
    }
    const preset = this.availableViewPresets().find((p) => p.uuid === uuid);
    if (preset) {
      this.onSelectedGridSettingsChange(preset);
    }
  }

  /** Display name; the default preset is suffixed so it is recognisable in the list and the tab strip. */
  private viewPresetLabel(preset: GridViewPreset): string {
    return preset.is_effective_default
      ? `${preset.name} ${this.translateService.instant('combobox.option-label-suffix.default')}`
      : preset.name;
  }

  /** Picker group header by owner scope; rows without a scope (legacy shape) are the caller's own. */
  private viewPresetGroupLabel(preset: GridViewPreset): string {
    switch (preset.view_preset_scope_id) {
      case GridViewPresetScope.SYSTEM:
        return this.translateService.instant(
          'grid.toolbar.view-preset-group.system',
        );
      case GridViewPresetScope.COMPANY:
        return this.translateService.instant(
          'grid.toolbar.view-preset-group.company',
        );
      default:
        return this.translateService.instant(
          'grid.toolbar.view-preset-group.mine',
        );
    }
  }

  /**
   * The toolbar's star: make the selected preset the one this grid opens with, or
   * un-default it. Selecting a preset is a temporary choice for this visit and
   * never touches the flag - the default is an explicit decision, one click here.
   * One PATCH /view_preset/set_flags call; the backend keeps a single default per
   * user + company + view.
   */
  toggleSelectedViewPresetDefault(): void {
    const preset = this.selectedViewPreset();
    if (!preset) {
      return;
    }
    const flags = { is_default: !preset.is_default };
    if (!preset.is_locked) {
      this.presetStore.setFlags(preset, flags);
      return;
    }
    if (!flags.is_default) {
      this.presetStore.setSharedFlags(preset, flags);
      return;
    }
    // A shared default resets everyone's own default for this view: confirmed first.
    const message = this.translateService.instant(
      'grid.toolbar.confirm.set-shared-view-preset-as-default',
      { name: preset.name, scope: this.viewPresetGroupLabel(preset) },
    );
    this.adapter.confirm(message).subscribe((confirmed) => {
      if (confirmed) {
        this.presetStore.setSharedFlags(preset, flags);
      }
    });
  }

  /**
   * The toolbar's pin: show the selected preset as a tab under the grid title (or
   * take it off the strip). Same flags endpoint, one call; the cache patch feeds
   * the tab strip through `estimateTabsFromViewPresets`.
   */
  toggleSelectedViewPresetPinned(): void {
    const preset = this.selectedViewPreset();
    if (!preset) {
      return;
    }
    const flags = { is_pinned: !preset.is_pinned };
    if (preset.is_locked) {
      this.presetStore.setSharedFlags(preset, flags);
    } else {
      this.presetStore.setFlags(preset, flags);
    }
  }

  onSelectedGridSettingsChange(selectedPreset: GridViewPreset) {
    this.adapter.logger.debug(
      'Selected grid settings changed:',
      selectedPreset,
    );
    this.selectedViewPresetId.set(selectedPreset?.uuid ?? null);

    if (selectedPreset) {
      // Apply filter model
      this.gridApi.setFilterModel(
        this.withStringSetFilterKeys(selectedPreset.settings.filterModel),
      );

      // Apply the column state
      this.gridApi.applyColumnState({
        state: selectedPreset.settings.columnState as ColumnState[],
        applyOrder: true,
      });

      this.gridApi.sizeColumnsToFit();

      this.onApplyExtraViewPresetSettings?.(selectedPreset.settings ?? {});

      this.currentTabId = selectedPreset.uuid;
    } else {
      this.currentTabId = null;
    }
  }

  /** The `settings` object a preset stores: the grid's column state and filter model plus the view's extra keys. */
  protected collectViewPresetSettings(): ViewPresetSettings {
    return {
      columnState: this.gridApi.getColumnState(),
      filterModel: this.gridApi.getFilterModel(),
      ...(this.currentExtraViewPresetSettings() ?? {}),
    };
  }

  /**
   * Combobox cleared: the grid returns to what it opens with (the default or the
   * first pinned preset, else the baseline). The default flag is untouched.
   */
  onSelectedGridSettingsClear() {
    this.applyOpeningPresetOrBaseline();
  }

  createViewPresetSettings() {
    this.isCreatingNew = true;
    this.showSaveViewPresetPopup();
  }

  editSelectedViewPreset() {
    const preset = this.selectedViewPreset();
    if (preset) {
      this.isCreatingNew = false;
      this.showSaveViewPresetPopup(preset);
    }
  }

  /**
   * "Save as company / system preset": the grid's current column state and filters
   * become a new shared row of the given scope, unbound (every object of the view),
   * named in the same dialog the personal presets use. Admin levels are re-checked
   * by the backend; the mutation toasts the outcome.
   */
  saveAsSharedViewPreset(scopeId: GridViewPresetScope): void {
    this.presetStore.askDetails().subscribe((details) => {
      if (details) {
        this.presetStore.createShared({
          view_preset_scope_id: scopeId,
          view_id: this.viewId,
          name: details.name,
          is_pinned: details.is_pinned,
          is_default: details.is_default,
          settings: this.collectViewPresetSettings(),
        });
      }
    });
  }

  /**
   * "Promote to company / system preset": copies the selected personal preset into
   * the given shared scope as a new, unflagged row; the personal row stays.
   */
  promoteSelectedViewPreset(scopeId: GridViewPresetScope): void {
    const preset = this.selectedViewPreset();
    if (preset && !preset.is_locked) {
      this.presetStore.promote(preset, scopeId);
    }
  }

  /**
   * The mutation owns the cache upsert and the toasts; the per-call callback only
   * moves this grid's selection onto the saved row (dropped if the grid is gone,
   * which is fine - there is nothing left to select in). A row without a uuid is a
   * create and goes through PUT /view_preset/create, never update_by_id.
   */
  saveViewPreset(viewPreset: GridViewPreset): void {
    this.presetStore.save(viewPreset, (savedViewPreset) => {
      this.selectedViewPresetId.set(savedViewPreset.uuid);
      this.currentTabId = savedViewPreset.uuid;
    });
  }

  showSaveViewPresetPopup(viewPreset?: GridViewPreset) {
    const sourceIsLocked = !!viewPreset?.is_locked;
    this.presetStore.askDetails(viewPreset).subscribe((details) => {
      if (details) {
        this.saveViewPreset({
          view_id: this.viewId,
          name: details.name,
          is_pinned: details.is_pinned,
          is_default: details.is_default,
          uuid: details.uuid && !sourceIsLocked ? details.uuid : null,
          settings: this.collectViewPresetSettings(),
        });
      } else {
        this.adapter.logger.debug(
          'Save view settings popup closed without saving.',
        );
        this.isCreatingNew = false;
      }
    });
  }

  /**
   * Copy on write: a locked (System / Company) preset is never updated in place -
   * the backend answers 403 and the save fails silently. Its identifiers are
   * dropped and the context becomes the user, so the save lands as the caller's
   * own preset through PUT /view_preset/create (WAP-1631).
   */
  saveSelectedViewPreset() {
    const preset = this.selectedViewPreset();
    if (preset) {
      const settings = this.collectViewPresetSettings();
      this.saveViewPreset(
        preset.is_locked
          ? {
              ...preset,
              id: null,
              uuid: null,
              context_object_id: null,
              context_object_class_id: null,
              settings,
            }
          : { ...preset, settings },
      );
    }
  }

  saveSelectedViewPresetAsNew() {
    this.presetStore.askDetails().subscribe((details) => {
      if (details) {
        this.saveViewPreset({
          ...this.selectedViewPreset(),
          id: null, // Ensure a new ID is generated
          uuid: null, // Ensure a new ID is generated
          view_id: this.viewId,
          context_object_id: null,
          context_object_class_id: null,
          name: details.name,
          is_pinned: details.is_pinned,
          is_default: details.is_default,
          settings: this.collectViewPresetSettings(),
        });
      } else {
        this.adapter.logger.debug(
          'Save view settings popup closed without saving.',
        );
        this.isCreatingNew = false;
      }
    });
  }

  /** Child routes swap the grid for the routed object editor (`objectEditorMode`). */
  setupRouting(objectName: string) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        switchMap(() => {
          // Traverse up to find the first child that has data
          let route = this.route;
          while (route && route.firstChild) {
            route = route.firstChild;
            if (
              route.snapshot.data[objectName] ||
              route.snapshot.data['createObject']
            ) {
              break;
            }
          }
          return route.data;
        }),
        tap((data: any) => {
          if (data) {
            if (data[objectName]) {
              this.objectEditorMode = true;
              this.displayMode = 'edit';
              this.object = data[objectName];
            } else if (data.createObject) {
              this.displayMode = 'create';
              this.objectEditorMode = true;
              this.object = null;
            } else {
              this.displayMode = 'list';
              this.object = null;
              this.objectEditorMode = false;
            }
          } else {
            this.objectEditorMode = false;
            this.displayMode = 'list';
            this.object = null;
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // ---- SSRM datasource ----

  /** True while the last row load failed and the error overlay is showing. */
  private dataLoadFailed = false;
  /** The view's own no-rows template, held while the error overlay replaces it. */
  private noRowsTemplateBeforeError: string | undefined;

  createServerSideDatasource(): IServerSideDatasource {
    let currentRequest$: Subscription | null = null;
    let currentParams: IServerSideGetRowsParams | null = null;

    return {
      getRows: (params: IServerSideGetRowsParams) => {
        this.lastGetRowsParams = params;

        // * Cancel the previous request if it's still ongoing
        // * This is needed because getRows is triggered outside Angular's lifecycle
        // * and we must handle unsubscription manually between calls
        if (currentRequest$ && !currentRequest$.closed) {
          currentRequest$.unsubscribe();
          // A cancelled load must still report fail(): ag-grid's lazy block
          // loader counts it as outbound until success/fail is called, and with
          // maxConcurrentDatasourceRequests: 1 a leaked slot permanently stops
          // every further block load (rows hang on "loading", scrolling and
          // page jumps stop fetching).
          currentParams?.fail();
        }
        currentParams = params;

        this.loading.set(true);

        currentRequest$ = this.fetchRows(params.request)
          .pipe(
            tap((rowsData) => {
              // * Duplicate row ids blow up inside params.success() (after the
              // * HTTP layer, so catchError never sees it) and leave the block
              // * as "ERR" skeletons - detect them up front and fail readably.
              const duplicateIds = this.findDuplicateRowIds(
                params.api,
                rowsData?.rowData,
              );
              if (duplicateIds.length > 0) {
                this.failDataLoad(
                  params,
                  `duplicate row ids in the server response: ${duplicateIds.join(', ')}`,
                );
                return;
              }

              this.clearDataLoadErrorOverlay(params.api);
              params.success(rowsData);
              this.firstLoadSettled.set(true);
              // The backend total drives the paginator; getDisplayedRowCount()
              // only knows the blocks loaded so far.
              this.totalCount.set(rowsData?.rowCount ?? 0);
              this.rowCount = this.gridApi.getDisplayedRowCount();
              params.api.sizeColumnsToFit();
              this.loading.set(false);
            }),
            catchError((error) => {
              this.failDataLoad(params, error);
              return EMPTY;
            }),
          )
          .subscribe();
      },
    };
  }

  /**
   * Default valueFormatter: numeric gaps read as 0, everything else as the
   * em-dash placeholder; a present value passes through unchanged.
   */
  private formatEmptyValue(params: ValueFormatterParams): string {
    let text: string;
    if (isEmptyCellValue(params.value)) {
      text = isNumericColumn(params.colDef) ? '0' : EMPTY_VALUE_PLACEHOLDER;
    } else {
      text = String(params.value);
    }
    return text;
  }

  /**
   * Default tooltip: only on true gaps (null / ''), never on a real 0. Text
   * columns also name the filter that isolates empty rows.
   */
  private emptyValueTooltip(params: ITooltipParams): string | undefined {
    let tooltip: string | undefined;
    if (isEmptyCellValue(params.value)) {
      tooltip = this.translateService.instant(
        isNumericColumn(params.colDef)
          ? 'grid.tooltip.no-value'
          : 'grid.tooltip.no-value-filter-blank',
      );
    }
    return tooltip;
  }

  /**
   * Resolves a failed load without params.fail() - ag-grid renders a failed
   * lazy block as a bare "ERR" skeleton in every cell. The block resolves as
   * empty with a readable overlay instead; the next load (filter change,
   * refresh) replaces both.
   */
  private failDataLoad(params: IServerSideGetRowsParams, error: unknown): void {
    params.success({ rowData: [], rowCount: 0 });
    this.firstLoadSettled.set(true);
    const forbidden = this.isForbiddenError(error);
    this.showDataLoadErrorOverlay(
      params.api,
      forbidden
        ? 'grid.overlay.no-permission-to-view-this-data'
        : 'grid.overlay.error-loading-the-data-rows',
    );
    this.rowCount = 0;
    this.totalCount.set(0);
    this.loading.set(false);
    if (!forbidden) {
      console.error('Failed to load grid data:', error);
    }
  }

  /**
   * SensikaHttpInterceptor rethrows a normalized `{ httpCode, ... }` object;
   * the raw HttpErrorResponse shape is kept as a fallback. A 403 is a
   * permission answer, not a failure, so it gets its own overlay text and
   * no console error.
   */
  private isForbiddenError(error: unknown): boolean {
    const status =
      error instanceof HttpErrorResponse
        ? error.status
        : (error as { httpCode?: number } | null)?.httpCode;
    return status === HTTP_FORBIDDEN;
  }

  /**
   * Row ids that occur more than once in an incoming block. Only meaningful
   * for grids that define getRowId (without it ag-grid generates unique ids).
   */
  private findDuplicateRowIds(api: GridApi, rows: unknown): string[] {
    const getRowId = api.getGridOption('getRowId');
    if (!getRowId || !Array.isArray(rows)) return [];

    const seen = new Set<string>();
    const duplicates = new Set<string>();
    try {
      for (const data of rows) {
        const id = String(getRowId({ data } as any));
        if (seen.has(id)) {
          duplicates.add(id);
        } else {
          seen.add(id);
        }
      }
    } catch {
      // A getRowId needing more context than {data} can't be pre-checked -
      // fall through to ag-grid's own handling rather than false-fail.
      return [];
    }
    return [...duplicates];
  }

  /**
   * Swaps the no-rows overlay for a translated load-error message. The view's
   * own `overlayNoRowsTemplate` (some grids ship a custom empty state) is
   * captured so a later successful load can restore it.
   */
  private showDataLoadErrorOverlay(
    api: GridApi,
    messageKey: string = 'grid.overlay.error-loading-the-data-rows',
  ): void {
    if (!this.dataLoadFailed) {
      this.dataLoadFailed = true;
      this.noRowsTemplateBeforeError = api.getGridOption(
        'overlayNoRowsTemplate',
      );
    }

    const message = this.translateService
      .instant(messageKey)
      .replace(/[&<>"]/g, (c: string) => `&#${c.charCodeAt(0)};`);
    api.updateGridOptions({
      overlayNoRowsTemplate: `<span class="ag-overlay-no-rows-center sensika-grid-load-error">${message}</span>`,
    });
    api.showNoRowsOverlay();
  }

  /**
   * Restores the view's own no-rows overlay after a load recovers. The error
   * overlay was shown manually (showNoRowsOverlay), and ag-grid keeps a manual
   * overlay up until hideOverlay() - without it the error text stayed over the
   * freshly loaded rows (WAP-1654). Hiding hands control back to ag-grid, which
   * shows the regular no-rows overlay itself when the recovered load is empty.
   */
  private clearDataLoadErrorOverlay(api: GridApi): void {
    if (!this.dataLoadFailed) return;

    this.dataLoadFailed = false;
    api.updateGridOptions({
      overlayNoRowsTemplate: this.noRowsTemplateBeforeError,
    });
    api.hideOverlay();
  }

  setupGridOptions() {
    this.gridOptions.onGridReady = (params) => {
      this.gridApi = params.api;
      params.api.sizeColumnsToFit();
      this.rowCount = this.gridApi.getDisplayedRowCount(); // Get total rows

      // The grid as configured by its column defs (and any initialState seed),
      // before any preset lands: the reference for "changed" with no preset.
      this.baselineSnapshot = this.snapshotGridSettings();
      this.gridReady.set(true);
      this.gridApi.addEventListener(
        'stateUpdated',
        this.onGridStateUpdated.bind(this),
      );
      if (this.checkboxSelection) {
        this.gridApi.addEventListener('selectionChanged', (event) =>
          this.checkboxSelectedIds.set(
            event.api
              .getSelectedNodes()
              .map((node) => node.id)
              .filter((id): id is string => id !== undefined),
          ),
        );
        this.gridApi.addEventListener('filterChanged', (event) =>
          event.api.deselectAll(),
        );
      }

      // Presets may already be cached (re-navigation), in which case the effect
      // in the constructor ran before the grid existed and skipped. This also
      // attaches the datasource once the opening preset is resolved.
      this.estimateTabsFromViewPresets(this.availableViewPresets());
    };
  }

  /**
   * State slices that never count as a change to a view preset: grid bootstrap
   * and programmatic `setState`, plus sizing / viewport / selection noise.
   */
  private static readonly VIEW_PRESET_NEUTRAL_STATE_SOURCES = new Set<string>([
    'gridInitializing',
    'api',
    'columnSizing',
    'scroll',
    'focusedCell',
    'pagination',
    'rangeSelection',
    'cellSelection',
    'rowSelection',
    'rowPinning',
    'rowGroupExpansion',
    'sideBar',
  ]);

  /** One event for every change a preset cares about (filter, sort, order, visibility, pinning). */
  private onGridStateUpdated(event: StateUpdatedEvent): void {
    const relevant = event.sources.some(
      (source) =>
        !DataGridComponent.VIEW_PRESET_NEUTRAL_STATE_SOURCES.has(source),
    );
    if (relevant && this.gridApi) {
      this.gridStateVersion.update((version) => version + 1);
    }
  }

  private snapshotGridSettings(): ViewPresetSettingsSnapshot {
    return this.normalizeViewPresetSettings(this.collectViewPresetSettings());
  }

  /**
   * The comparable form of a preset's `settings` (stored or live). `core`: column
   * order, visibility, sort and pinning - never width, resizing is per-screen
   * noise - plus the filter model with set-filter keys stringified and key order
   * neutralized, so the same settings always produce the same string. `extra`:
   * every other key (the view's `extraViewPresetSettings`), key order neutralized;
   * `null` when the settings carry none.
   */
  private normalizeViewPresetSettings(
    settings: ViewPresetSettings | null | undefined,
  ): ViewPresetSettingsSnapshot {
    const { columnState, filterModel, ...extraSettings } = settings ?? {};
    const columns = (columnState ?? []).map((column) => ({
      colId: column.colId,
      hide: !!column.hide,
      sort: column.sort ?? null,
      sortIndex: column.sortIndex ?? null,
      pinned: column.pinned ?? null,
    }));
    const filters = sortDeepForComparison(
      this.withStringSetFilterKeys(filterModel ?? {}) ?? {},
    );
    const extra =
      Object.keys(extraSettings).length > 0
        ? JSON.stringify(sortDeepForComparison(extraSettings))
        : null;

    return { core: JSON.stringify({ columns, filters }), extra };
  }

  resetGridSettings() {
    if (this.gridApi) {
      this.gridApi.setFilterModel(null);
      this.gridApi.resetColumnState();
      this.gridApi.sizeColumnsToFit();
    }
  }

  deleteSelectedGridSettings(): void {
    const preset = this.selectedViewPreset();
    if (!preset) return;

    const message = this.translateService.instant(
      'grid.settings.delete.confirmation-message',
    );

    this.adapter.confirm(message).subscribe((confirmed) => {
      if (confirmed) {
        // Cache removal + toasts live on the mutation; this grid only drops its
        // selection and returns the columns/filters to their initial state.
        this.presetStore.delete(preset, () => {
          // The cache patch may not have reached the signal yet: never
          // re-open with the row just deleted.
          this.applyOpeningPresetOrBaseline(preset.uuid);
        });
      } else {
        this.adapter.logger.debug('Delete grid settings cancelled.');
      }
    });
  }

  handleAgGridParamsChange(changes: SimpleChanges = null): void {
    this.adapter.logger.debug('AgGrid parameters changed.', changes);
  }

  getGridOptions(): GridOptions {
    return {
      rowModelType: 'serverSide',
      cacheBlockSize: this.pageSize, // One block is exactly one page
      maxBlocksInCache: 2, // Store a maximum of 2 blocks in memory
      blockLoadDebounceMillis: 100, // Debounce the block loading by 100ms
      infiniteInitialRowCount: 5, // Initial number of rows displayed
      // Paging is on, but ag-grid's panel stays suppressed below - the toolbar's
      // <sui-pagination> is the control, driven via paginationGoToPage. Views
      // that opt out keep the original infinite scroll.
      pagination: this.supportsToolbarPagination(),
      paginationPageSize: this.pageSize,
      onPaginationChanged: () => {
        if (this.gridApi) {
          this.currentPage.set(this.gridApi.paginationGetCurrentPage() + 1);
        }
      },

      suppressContextMenu: true, // Context menu is only usable with the required Enterpise modules registered
      enableCellTextSelection: false, // TRUE to enable selection of cell's contents (without Enterprise context menu users cannot copy values)

      suppressPaginationPanel: true, // Hide pagination panel for infinite scrolling
      suppressRowTransform: true, // Disable row transformation for infinite scrolling
      animateRows: false, // Disable row animations for infinite scrolling
      rowHeight: 40,

      defaultColDef: {
        flex: 1,
        sortable: true,
        // First header click sorts desc (log grids are newest-first); grid-level
        // sortingOrder is deprecated since ag-grid v33.
        sortingOrder: ['desc', 'asc'],
        filter: true,
        floatingFilter: true,
        resizable: true,
        // Empty-value convention (WAP-1643): a column's own valueFormatter,
        // cellClassRules or tooltipValueGetter overrides these.
        valueFormatter: (params) => this.formatEmptyValue(params),
        cellClassRules: {
          [EMPTY_CELL_CLASS]: (params) =>
            isNumericColumn(params.colDef)
              ? isEmptyNumeric(params.value)
              : isEmptyCellValue(params.value),
        },
        tooltipValueGetter: (params) => this.emptyValueTooltip(params),
      },
      popupParent: agGridBodyPopupParent(),
      maxConcurrentDatasourceRequests: 1,
      onFilterChanged: (event) => {
        this.handleAgGridParamsChange();
      },
      onSortChanged: (event) => {
        this.handleAgGridParamsChange();
      },
      // Keyboard: ag-grid moves the focused cell itself; the details pane of a
      // master-detail grid follows that focus (WAP-1691).
      onCellFocused: (event) => this.followFocusedRow(event),
      getRowId: (params) => params.data.id,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
            },
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
          },
        ],
      },
    };
  }

  /**
   * Whether this view pages through the toolbar paginator. It needs the fetch
   * response to carry the backend total as `rowCount` - without one there is no
   * page count, so a view whose endpoint cannot report a total keeps infinite
   * scroll via `paginated = false`.
   *
   * * A method, not the input directly: `getGridOptions()` runs in the
   * * constructor, before inputs exist (undefined -> true); ngOnInit re-patches
   * * `gridOptions.pagination` with the real input value.
   */
  protected supportsToolbarPagination(): boolean {
    return this.paginated ?? true;
  }

  // ---- Current row ----

  /**
   * The details pane of a master-detail grid (`trackRowSelection`) follows the
   * current row: the one clicked or, with the keyboard, the one holding the
   * focused cell (ag-grid's own arrow navigation; `.ag-row-focus` marks it, see
   * the component stylesheet). Row *selection* is never touched on a grid with
   * checkbox selection - WAP-1691: a window-level ArrowUp / ArrowDown handler
   * used to deselectAll() + setSelected() on its own row counter, rewriting the
   * checkboxes of every mounted grid (from any focus on the page) and drifting
   * from ag-grid's focused cell. Grids without a `rowSelection` config keep the
   * single click-selection highlight they always had.
   */
  protected markCurrentRow(rowNode: IRowNode | null | undefined): void {
    if (!rowNode?.data) return;
    if (!this.gridOptions.rowSelection) {
      this.gridApi.deselectAll();
      rowNode.setSelected(true);
    }
    if (!this.trackRowSelection || rowNode.id === this.currentRowId) return;
    this.currentRowId = rowNode.id ?? null;
    this.selectedObject = rowNode.data;
    this.emitSelectedRow();
  }

  /** Keyboard path of the details pane: a focused body cell makes its row current. */
  private followFocusedRow(event: CellFocusedEvent): void {
    if (!this.trackRowSelection || event.rowIndex == null || event.rowPinned) {
      return;
    }
    const rowNode = this.gridApi.getDisplayedRowAtIndex(event.rowIndex);
    if (!rowNode?.data || rowNode.id === this.currentRowId) return;
    this.markCurrentRow(rowNode);
  }

  /**
   * Click path: every click reaches `rowClicked`; a click on the selection
   * checkbox only toggles the bulk scope and never makes the row current.
   */
  onObjectSelected(event: RowClickedEvent): void {
    this.rowClicked.emit(event);

    const target = event.event?.target as HTMLElement | null;
    if (target?.closest?.(SELECTION_CHECKBOX_SELECTOR)) {
      return;
    }
    if (this.trackRowSelection) {
      event.event?.preventDefault(); // Prevent the default event behavior
    }
    this.markCurrentRow(event.node);
  }

  private emitSelectedRow(): void {
    if (this.trackRowSelection) {
      this.selectedRowDataChange.emit(this.selectedObject ?? null);
    }
  }

  onDisplayedColumnsChanged(): void {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  protected onCellValueChanged(event: CellValueChangedEvent): void {
    this.cellValueChanged.emit(event);
  }

  // ---- Tabs ----

  /** NgpTabset is value-based: the tab id is the preset uuid. */
  handleTabValueChange(tabId: string): void {
    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab) {
      return;
    }
    this.currentTabId = tab.id;

    if (tab.is_preset) {
      const selectedPreset = this.availableViewPresets().find(
        (preset) => preset.uuid === tab.id,
      );
      if (selectedPreset) {
        this.onSelectedGridSettingsChange(selectedPreset);
      }
    }
  }

  /** The tab's own unpin control - no combobox round trip. Same flags endpoint as the toolbar pin. */
  unpinViewPreset(uuid: string): void {
    const preset = this.availableViewPresets().find((p) => p.uuid === uuid);
    if (!preset) {
      return;
    }
    this.presetStore.setFlags(preset, { is_pinned: false });
  }

  // ---- Toolbar actions ----

  exportGridAsCsv(): void {
    if (this.onExportCsv) {
      this.onExportCsv();
    } else if (this.gridApi) {
      this.gridApi.exportDataAsCsv();
    }
  }

  exportGridAsCsvServerSide(): void {
    this.onExportCsvServerSide?.();
  }

  /**
   * Toolbar Refresh: drop any feature-level cache (the `onInvalidateCaches`
   * callback), then re-fetch every block from the server.
   */
  refreshGrid(): void {
    this.onInvalidateCaches?.();
    this.purgeRows();
  }

  /**
   * Toolbar Reset Filters: clear the column filters only - column order,
   * visibility and sort survive (that is `resetGridSettings()`'s job). Safe to
   * call with nothing filtered, so the button stays enabled.
   */
  resetGridFilters(): void {
    this.gridApi?.setFilterModel(null);
  }

  /**
   * Set-filter values as strings, which is how ag-grid keys its filter list
   * (`keyCreator` returns a string).
   *
   * * A stored view preset keeps whatever the model looked like when it was
   * * saved, and presets saved while `keyCreator` still returned the raw numeric
   * * nomenclature id carry numbers. Those no longer match the string keys, so
   * * the column renders as nothing-selected - `(0)` - even though the model
   * * reaches the backend untouched and the rows come back correctly filtered.
   * * Coercing on the way in repairs those presets; it is a no-op for any saved
   * * since. `null` is preserved: it is ag-grid's (Blanks) entry, not a value.
   */
  protected withStringSetFilterKeys(
    model: {
      [colId: string]: any;
    } | null,
  ): { [colId: string]: any } | null {
    if (!model) {
      return model;
    }

    const normalized: { [colId: string]: any } = {};
    for (const [colId, condition] of Object.entries(model)) {
      const values = condition?.values;
      normalized[colId] =
        condition?.filterType === 'set' && Array.isArray(values)
          ? {
              ...condition,
              values: values.map((value: unknown) =>
                value === null ? null : String(value),
              ),
            }
          : condition;
    }

    return normalized;
  }

  /** Paginator -> grid: jump to a 1-based page over the server-side row model. */
  goToPage(page: number): void {
    if (!this.gridApi || !Number.isFinite(page)) {
      return;
    }
    const maxPage = Math.max(1, Math.ceil(this.totalCount() / this.pageSize));
    const target = Math.min(Math.max(1, Math.round(page)), maxPage);
    // currentPage follows from onPaginationChanged, not set here.
    this.gridApi.paginationGoToPage(target - 1);
  }
}
